import React, { useState } from 'react';
import {
  FDBox,
  FDDeleteButton,
  FDDialogActions,
  FDDialogContent,
  FDDialogTitle,
  FDEmptyState,
  FDPrimaryActionButton,
  FDStack,
  FDTable,
  FDTableBody,
  FDTableHead,
  FDTableRow,
  FDTextButton,
} from '@facet-wealth/design';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import {
  QuestionnaireTableCell,
  QuestionnaireTableCellNumber,
} from 'Components/Universal/Questionnaire/UI/QuestionnaireTable/TableCell';
import PropTypes from 'prop-types';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { getDynamicDateTypeValue } from 'Components/Universal/Questionnaire/utils';
import MetaLayoutRemainingFields from 'Components/UI/Meta/MetaForm/Components/MetaLayout/MetaLayoutRemainingFields';
import { currency } from 'Helpers/Formats';
import { IncomeDialog } from './IncomeDialog';

// We want these fields to be hidden from the table but still be available in the create/edit modal.
// This honestly isn't the ideal way of handling hiding these fields, but we're just doing it
// this way to avoid scope creep for now.
const hiddenFields = [
  'socialSecurityBenefitType',
  'company',
  'benefitElection',
  'netMonthlyIncome',
  'survivorshipRate',
  'numberOfYears',
  'amountIsPerFrequency',
  'frequencyGrossValue',
  'startType',
  'endType',
  'startAtAge',
  'endAtAge',
  'birthDate',
  'isTaxFree',
  'affectedByInflation',
];

// these fields may get excluded due to excludedUnless,
// but we always want these columns in the table
const shownFields = [
  'annualGrossValue',
];

const dynamicDateTypeFields = [
  'startDate',
  'endDate',
];

const metaOverrideSet = buildFacetDesignOverride()
  .nameOverride(shownFields, { hidden: false }, overrideReadOnly)
  .nameOverride(hiddenFields, { hidden: true }, overrideReadOnly)
  .overrideAll({ readonly: true, _component: () => QuestionnaireTableCell }, overrideReadOnly)
  .override({ readonly: true, type: 'number' }, { _component: () => QuestionnaireTableCellNumber }, overrideReadOnly);

function IncomeDialogLayout({ onCancel, isEdit, resource }) {
  const { onDelete, onSubmit } = useMetaLayoutContext();

  return (
    <>
      <FDDialogTitle value={isEdit ? 'Edit Income' : 'Add Income'} />
      <FDDialogContent>
        <FDBox minWidth={500}>
          <FDStack spacing={2} direction={'column'} alignItems={'stretch'}>
            <FDStack spacing={2}>
              <MetaControlField attr={'clientId'} />
            </FDStack>
            <FDStack spacing={2}>
              <MetaControlField attr={'description'} />
            </FDStack>
            <FDStack spacing={2}>
              <MetaControlField attr={'frequencyGrossValue'} />
              <MetaControlField attr={'annualGrossValue'} />
              <MetaControlField attr={'amountIsPerFrequency'} />
            </FDStack>
            <FDStack spacing={2}>
              <MetaControlField attr={'isTaxFree'} />
              <MetaControlField attr={'affectedByInflation'} />
            </FDStack>
            <MetaLayoutRemainingFields resource={resource} noAccordion />
            <FDStack spacing={2}>
              <MetaControlField attr={'startType'} />
              <MetaControlField attr={'startDate'} />
              <MetaControlField attr={'startAtAge'} />
            </FDStack>
            <FDStack spacing={2}>
              <MetaControlField attr={'endType'} />
              <MetaControlField attr={'endDate'} />
              <MetaControlField attr={'endAtAge'} />
            </FDStack>
            <FDBox alignSelf={'center'}>
              {isEdit ? (
                <FDDeleteButton
                  onClick={onDelete}
                />
              ) : null}
            </FDBox>
          </FDStack>
        </FDBox>
      </FDDialogContent>
      <FDDialogActions>
        <FDPrimaryActionButton
          onClick={onSubmit}
          label="Submit"
        />
        <FDTextButton
          onClick={onCancel}
          label="Cancel"
        />
      </FDDialogActions>
    </>
  );
}

const IncomeDetailsTable = (props) => {
  const { collection, refreshCollection } = props;
  const resourceList = collection.resources().list();
  const [income, setIncome] = useState();

  const emptyRow = (
    <FDBox spacing={1} alignSelf={'center'}>
      <FDEmptyState icon={'search'} title={'No results'} description={'No income found for the selected household'} />
    </FDBox>);

  function filterFn(field) {
    // always shown
    if (shownFields.includes(field.attr('name'))) {
      return true;
    }

    return !(field.attr('hidden') || hiddenFields.includes(field.attr('name')));
  }

  function getHidden(field) {
    // never hidden
    if (shownFields.includes(field.attr('name'))) {
      return false;
    }

    // always hidden
    if (hiddenFields.includes(field.attr('name'))) {
      return true;
    }

    return field.attr('hidden');
  }

  return (
    <>
      <FDStack direction="column" spacing={2} alignItems="flex-end">
        <FDPrimaryActionButton
          label="Add Income"
          icon="add"
          onClick={() => setIncome(collection.create())}
        />
        <FDTable>
          <FDTableHead>
            <FDTableRow>
              {// This isn't ideal, but we would need to add a new endpoint for this list view and that doesn't feel worth
                // it for this questionnaire at this point, so we're filtering the fields we want to hide from the table here.
                collection.meta().template().list().filter(field => filterFn(field))
                  .map((field) => {
                    return (<QuestionnaireTableCell key={field.attr('name')} value={field.attr('prompt')} hidden={getHidden(field)} />);
                  })}
            </FDTableRow>
          </FDTableHead>
          <FDTableBody>
            {resourceList.map((resource) => {
              // This is a hack to get the start and end date values to display in the table.
              // These values for these fields are driven by excludedUnless logic, but that isn't supported here
              const { key: startKey, value: startValue } = getDynamicDateTypeValue(resource, 'start');
              const { key: endKey, value: endValue } = getDynamicDateTypeValue(resource, 'end');

              return (
                <FDTableRow
                  key={`${resource.id()}`}
                  onClick={() => setIncome(resource.copy())}
                  hover
                >
                  {resource.meta().template().list()
                    .filter(field => !dynamicDateTypeFields.includes(field.attr('name')))
                    .map((field) => {
                      // Due to a change of how MetaControl no longer allows hidden to be overridden from here
                      // we have to manually render the shown field so that it doesn't disappear with excluded unless
                      // for per frequency incomes
                      if (shownFields.includes(field.attr('name'))) {
                        return (
                          <QuestionnaireTableCellNumber key={field.attr('name')} value={currency(parseFloat(resource.attr(field.attr('name'))), false)} />
                        );
                      }
                      return (
                        <MetaControl
                          key={field.attr('name')}
                          hidden={getHidden(field)}
                          resource={resource}
                          attrName={field.attr('name')}
                          metaOverrideSet={metaOverrideSet}
                        />
                      );
                    })}
                  <QuestionnaireTableCell key={startKey} value={startValue} />
                  <QuestionnaireTableCell key={endKey} value={endValue} />
                </FDTableRow>
              );
            })}
          </FDTableBody>
        </FDTable>
        {collection.resources().empty() ? emptyRow : null}
        {income
          ? <IncomeDialog
            metaLayout={IncomeDialogLayout}
            resource={income}
            onCancel={() => setIncome(null)}
            onSubmit={() => {
              setIncome(null);
              refreshCollection();
            }}
            onDelete={() => {
              setIncome(null);
              refreshCollection();
            }}
          />
          : null}
      </FDStack>
    </>
  );
};

IncomeDetailsTable.propTypes = {
  collection: PropTypes.object.isRequired,
  refreshCollection: PropTypes.func.isRequired,
};

export { IncomeDetailsTable };
